import React, { useState, useEffect } from "react";
import moment from "moment";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from "chart.js";
import { Bar, Pie, Doughnut } from "react-chartjs-2";
import { getLeadsByStatus, getLeadsBySource } from "@/actions/reports";
import { PieChart, BarChart3 } from "lucide-react";
import ReportHeader from "../components/ReportHeader";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

interface ChartData {
  labels: string[];
  data: number[];
}

const generateColors = (count: number) => {
  const colors = [
    "rgba(79, 70, 229, 0.8)", "rgba(16, 185, 129, 0.8)", "rgba(245, 158, 11, 0.8)",
    "rgba(239, 68, 68, 0.8)", "rgba(59, 130, 246, 0.8)", "rgba(168, 85, 247, 0.8)",
    "rgba(236, 72, 153, 0.8)", "rgba(20, 184, 166, 0.8)", "rgba(249, 115, 22, 0.8)",
    "rgba(132, 204, 22, 0.8)", "rgba(99, 102, 241, 0.8)", "rgba(244, 63, 94, 0.8)",
  ];
  return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
};

const ChartsReport: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [statusData, setStatusData] = useState<ChartData | null>(null);
  const [sourceData, setSourceData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, sourceRes] = await Promise.all([
        getLeadsByStatus({ from_date: fromDate, to_date: toDate }),
        getLeadsBySource({ from_date: fromDate, to_date: toDate }),
      ]);
      if (statusRes.success) setStatusData((statusRes.data as { chartData: ChartData }).chartData);
      if (sourceRes.success) setSourceData((sourceRes.data as { chartData: ChartData }).chartData);
    } catch (error) {
      console.error("Error fetching chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full">
        <ReportHeader title="Charts" subtitle="Visual analytics" icon={<PieChart className="h-5 w-5 text-white" />} fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} onApplyFilter={fetchData} />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
        </div>
      </div>
    );
  }

  const statusChartData = statusData ? {
    labels: statusData.labels,
    datasets: [{ label: "Leads by Status", data: statusData.data, backgroundColor: generateColors(statusData.labels.length), borderWidth: 1 }],
  } : null;

  const sourceChartData = sourceData ? {
    labels: sourceData.labels,
    datasets: [{ label: "Leads by Source", data: sourceData.data, backgroundColor: generateColors(sourceData.labels.length), borderWidth: 1 }],
  } : null;

  return (
    <div className="h-full flex flex-col">
      <ReportHeader title="Charts" subtitle="Visual analytics" icon={<PieChart className="h-5 w-5 text-white" />} fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} onApplyFilter={fetchData} />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Status Charts */}
          {statusChartData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="background-container rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-brandColor" />Leads by Status (Bar)</h3>
                <div className="h-80"><Bar data={statusChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} /></div>
              </div>
              <div className="background-container rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><PieChart className="h-5 w-5 text-brandColor" />Leads by Status (Pie)</h3>
                <div className="h-80"><Pie data={statusChartData} options={{ responsive: true, maintainAspectRatio: false }} /></div>
              </div>
            </div>
          )}

          {/* Source Charts */}
          {sourceChartData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="background-container rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-green-500" />Leads by Source (Bar)</h3>
                <div className="h-80"><Bar data={sourceChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} /></div>
              </div>
              <div className="background-container rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><PieChart className="h-5 w-5 text-green-500" />Leads by Source (Doughnut)</h3>
                <div className="h-80"><Doughnut data={sourceChartData} options={{ responsive: true, maintainAspectRatio: false }} /></div>
              </div>
            </div>
          )}

          {!statusChartData && !sourceChartData && (
            <div className="text-center py-8">
              <PieChart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No chart data available for the selected date range</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartsReport;
